import React from 'react'
import Accordion from '../../Accordion/Accordion'
import AccordionChild from '../../AccordionChild/AccordionChild'
import faqPageSchema from '@/utils/schema/faqPageSchema'
import { generateCommaSeparatedListEndingWithAnd } from '@/utils/generateCommaSeparatedListEndingWithAnd'
import { PageType } from '@/types/models/PageType.model'

type Term = {
  fieldValue: string
}

type Props = {
  quick_links: []
  staticHits: []
  pageType: PageType
  termData: {}
  terms?: {
    amenities?: Term[]
    options?: Term[]
    therapies?: Term[]
  }
  searchState: {}
  searchResults: {}
  pageData: {
    title?: string
  }
  facetData: {
    facet?: string
  }
  shouldDefaultRegionalize?: boolean
  centerCount: number
  termTitle: string
  customTermHeader: string
  compact?: boolean
}

const DefaultLocationFAQs: React.FC<Props> = ({
  quick_links,
  staticHits,
  pageType,
  searchState,
  searchResults,
  terms = {},
  pageData,
  facetData,
  shouldDefaultRegionalize = false,
  centerCount,
  termTitle,
  customTermHeader,
  compact,
  ...rest
}) => {
  const { amenities = [], options = [], therapies = [] } = terms

  const locationTerm = pageData.title
  const centerCountThreshold = 8

  const faqSchemaMarkup = faqPageSchema([
    {
      question: `How can I find a top-rated ${
        shouldDefaultRegionalize ? 'centre' : 'center'
      } in ${locationTerm}?`,
      answer: `1. Ask friends or family members for personal recommendations. 2. Let us help! Our directory includes ${centerCount} top-rated ${
        shouldDefaultRegionalize ? 'centres' : 'centers'
      } renowned for their clinical practice and patient experience. You can review photos, read reviews, and contact a ${
        shouldDefaultRegionalize ? 'centre' : 'center'
      } to start your path to recovery. 3. Ask a trusted doctor or therapist for a referral.`
    },
    {
      question: 'How do I know if I need rehab?',
      answer:
        'If you have a mental health condition or addiction that hinders your ability to function in your day-to-day life, looking into rehab could be a good idea. Residential rehab also offers a retreat from your normal environment that allows you to focus entirely on healing, which many people find beneficial even if their conditions are less severe.'
    },
    {
      question: `How do I choose the best ${
        shouldDefaultRegionalize ? 'centre' : 'center'
      } in ${locationTerm} for my needs?`,
      answer:
        'Consider the level of care (i.e., Detox, Inpatient, or Outpatient).'
    },
    {
      question: `What’s the typical cost for treatment in ${locationTerm}?`,
      answer: `Since many providers <a href='https://recovery.com/price/insurance-accepted/'>accept insurance</a>, rehab doesn't have to be expensive. If you don't have insurance but are able to travel, you can find <a href='https://recovery.com/price/affordable/'>affordable cash pay rehab ${
        shouldDefaultRegionalize ? 'centres' : 'centers'
      }</a> in destinations around the world.`
    },
    {
      question: `What is treatment like at a rehab ${
        shouldDefaultRegionalize ? 'centre' : 'center'
      } in ${locationTerm}?`,
      answer: `The most common treatment approaches are ${generateCommaSeparatedListEndingWithAnd(
        options
          .map((option) => option?.fieldValue?.toLowerCase())
          .filter(Boolean)
          .slice(0, 2)
      )}. You might find yourself taking part in ${generateCommaSeparatedListEndingWithAnd(
        therapies
          .map((therapy) => therapy?.fieldValue?.toLowerCase())
          .filter(Boolean)
          .slice(0, 2)
      )}.`
    },
    {
      question: `What should I expect to experience in rehab in ${locationTerm}?`,
      answer: `${locationTerm} treatment ${
        shouldDefaultRegionalize ? 'centres' : 'centers'
      } often have the following amenities: ${generateCommaSeparatedListEndingWithAnd(
        amenities
          .map((amenity) => amenity?.fieldValue?.toLowerCase())
          .filter(Boolean)
          .slice(0, 3)
      )}. They all provide evidence-based therapies, holistic treatments, and may offer various wellness services.`
    }
  ])

  const faqMarkupString = JSON.stringify(faqSchemaMarkup)

  return (
    <div>
      <script
        dangerouslySetInnerHTML={{ __html: faqMarkupString }}
        type="application/ld+json"
      />
      <Accordion>
        {centerCount > 0 && (
          <AccordionChild
            heading={`How can I find a top-rated ${
              shouldDefaultRegionalize ? 'centre' : 'center'
            } for treatment in ${locationTerm}?`}
            as={'h4'}
            compact={compact}
          >
            <ol className="list-decimal pl-4">
              <li className="mb-4">
                <p className="mb-2">
                  Ask friends or family members for personal recommendations.
                </p>
              </li>
              <li className="mb-4">
                <p className="mb-2">
                  Let Recovery.com help! Our directory includes {centerCount}{' '}
                  top-rated {shouldDefaultRegionalize ? 'centres' : 'centers'}.
                </p>
              </li>
              <li className="mb-4">
                <p className="mb-2">
                  Ask a trusted doctor or therapist for a referral.
                </p>
              </li>
            </ol>
          </AccordionChild>
        )}
        <AccordionChild
          heading="How do I know if I need rehab?"
          as={'h4'}
          compact={compact}
        >
          If you have a mental health condition or addiction that hinders your
          ability to function, considering rehab could be a good idea.
          Residential rehab also offers a retreat from your normal environment,
          allowing you to focus entirely on healing.
        </AccordionChild>

        <AccordionChild
          heading={`How do I choose the best ${
            shouldDefaultRegionalize ? 'centre' : 'center'
          } in ${locationTerm}?`}
          as={'h4'}
          compact={compact}
        >
          <ul>
            <li className="mb-4">
              <p className="mb-2">
                <span className="font-semibold">Level of care</span>: Consider
                Detox, Inpatient, or Outpatient care.
              </p>
            </li>
            <li className="mb-4">
              <p className="mb-2">
                <span className="font-semibold">Location</span>: Some prefer a
                location closer to home, while others benefit from traveling.
              </p>
            </li>
            <li className="mb-4">
              <p className="mb-2">
                <span className="font-semibold">Cost</span>: Consider insurance
                coverage, self-pay, or other funding options.
              </p>
            </li>
            <li className="mb-4">
              <p className="mb-2">
                <span className="font-semibold">Program length</span>: This
                depends on the severity of the condition and individual needs.
              </p>
            </li>
            <li className="mb-4">
              <p className="mb-2">
                <span className="font-semibold">Specializations</span>: Make
                sure the rehab facility specializes in the condition you're
                addressing.
              </p>
            </li>
          </ul>
        </AccordionChild>
        <AccordionChild
          heading={`What is the typical cost for treatment in ${locationTerm}?`}
          as={'h4'}
          compact={compact}
        >
          <p>
            Since many providers{' '}
            <a
              className="font-semibold underline"
              href="/price/insurance-accepted/"
            >
              {' '}
              accept insurance
            </a>
            , rehab doesn't have to be expensive. If you don't have insurance
            but are able to travel, you can find{' '}
            <a
              className="font-semibold underline"
              href="/price/budget/"
            >
              {' '}
              affordable cash pay rehab{' '}
              {shouldDefaultRegionalize ? 'centres' : 'centers'}{' '}
            </a>{' '}
            in destinations around the world.
          </p>
        </AccordionChild>
        {staticHits.length > centerCountThreshold && (
          <>
            <AccordionChild
              heading={`What is treatment like at a rehab in ${locationTerm}?`}
              as={'h4'}
              compact={compact}
            >
              <p>
                The most common treatment approaches are{' '}
                {generateCommaSeparatedListEndingWithAnd(
                  options
                    .map((option) => option?.fieldValue?.toLowerCase())
                    .filter(Boolean)
                    .slice(0, 2)
                )}
                . You might find yourself taking part in{' '}
                {generateCommaSeparatedListEndingWithAnd(
                  therapies
                    .map((therapy) => therapy?.fieldValue?.toLowerCase())
                    .filter(Boolean)
                    .slice(0, 2)
                )}
                .
              </p>
            </AccordionChild>
            <AccordionChild
              heading={`What should I expect to experience in rehab in ${locationTerm}?`}
              as={'h4'}
              compact={compact}
            >
              {locationTerm + ' '}
              treatment {shouldDefaultRegionalize ? 'centres' : 'centers'} often
              have the following amenities:{' '}
              {generateCommaSeparatedListEndingWithAnd(
                amenities
                  .map((amenity) => amenity?.fieldValue?.toLowerCase())
                  .filter(Boolean)
                  .slice(0, 3)
              )}
              . They all provide evidence-based therapies, holistic treatments,
              and may offer various wellness services.
            </AccordionChild>
          </>
        )}
      </Accordion>
    </div>
  )
}
export default DefaultLocationFAQs
